<template>
  <component
    :is="hasChevron ? 'a' : 'div'"
    :href="hasChevron ? button.href : undefined"
    :class="{ 'full-link' : hasChevron }"
    :tabindex="hasChevron ? 0 : -1"
    @click="clickOnCard"
  >
    <div
      :class="['zg-cross-sell', {
        [`zg-cross-sell--background-${background}`]: background
      }]"
    >
      <div
        v-if="illustration"
        :class="['illustration', {
          'illustration--on-clouds': illustrationOnClouds
        }]"
      >
        <svg
          v-if="illustrationOnClouds"
          width="178"
          height="121"
          viewBox="0 0 178 121"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="clouds"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M30.3828 -3.29096C44.5692 -19.7128 68.5077 -30.5 95.6488 -30.5C132.127 -30.5 162.819 -11.0145 171.827 15.4475C175.782 23.6127 178 32.7763 178 42.4579C178 76.7195 150.225 104.494 115.964 104.494C102.038 104.494 89.1843 99.9056 78.8314 92.1582C77.3709 91.9086 75.9255 91.6272 74.4965 91.3149C63.5027 108.689 44.1166 120.224 22.036 120.224C-12.2255 120.224 -40 92.45 -40 58.1884C-40 23.9268 -12.2255 -3.84766 22.036 -3.84766C24.8666 -3.84766 27.6528 -3.65809 30.3828 -3.29096Z"
            fill="white"
          />
        </svg>

        <zg-illustration
          :illustration="illustration"
        />
      </div>

      <div
        v-else-if="product"
        class="icon"
      >
        <zg-product-icon
          v-if="product"
          :product="product"
          size="xxxsmall"
        />
      </div>

      <div
        v-if="title"
        class="title"
      >
        <h4>
          {{ title }}
          <zg-icon
            v-if="hasChevron"
            icon="forward"
          />
        </h4>
      </div>

      <p
        v-if="subTitle"
        v-html="subTitle"
      />

      <zg-usps
        v-if="usps && usps.length"
        :usps="usps"
        direction="vertical"
      />

      <a
        v-if="button.href && !hasChevron"
        :href="button.href"
        class="button"
        @click="$emit('tracking', trackingObject === null ? {} : trackingObject)"
      >
        <zg-link-button
          v-if="hasLinkButton"
          :icons="linkType ? {
            right: linkType
          } : {}"
          :link="button.href"
        >
          {{ button.text }}
        </zg-link-button>

        <zg-button
          v-else
          full-width
        >
          {{ button.text }}
        </zg-button>
      </a>
    </div>
  </component>
</template>

<script>
  import ZgButton from '../ZgButton/ZgButton.vue'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgIllustration from '../ZgIllustration/ZgIllustration.vue'
  import ZgLinkButton from '../ZgLinkButton/ZgLinkButton.vue'
  import ZgProductIcon from '../ZgProductIcon/ZgProductIcon.vue'
  import ZgUsps from '../ZgUsps/ZgUsps.vue'

  export default {
    name: 'ZgCrossSell',
    components: {
      ZgUsps,
      ZgProductIcon,
      ZgButton,
      ZgIllustration,
      ZgIcon,
      ZgLinkButton
    },
    props: {
      background: {
        type: String,
        required: false,
        default: 'beige',
        validator: value => [
          'beige',
          'white',
          'brand-100'
        ].includes(value)
      },
      title: {
        type: String,
        required: false,
        default: ''
      },
      subTitle: {
        type: String,
        required: false,
        default: ''
      },
      usps: {
        type: Array,
        default: () => [],
        validator: usps => usps.every(usp => typeof usp === 'string')
      },
      button: {
        type: Object,
        default: () => ({})
      },
      linkType: {
        type: String,
        required: false,
        default: null
      },
      buttonType: {
        type: String,
        required: false,
        default: 'button',
        validator: value => [
          'button',
          'chevron',
          'link'
        ].includes(value)
      },
      product: {
        type: String,
        required: false,
        default: null
      },
      trackingObject: {
        type: Object,
        default: null,
        validator: value => {
          return value.event && value.category && value.action && value.label
        }
      },
      illustration: {
        type: String,
        required: false,
        default: null
      },
      illustrationOnClouds: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    computed: {
      hasChevron () {
        return this.buttonType === 'chevron'
      },
      hasLinkButton () {
        return this.buttonType === 'link'
      }
    },
    methods: {
      clickOnCard () {
        if (this.hasChevron) {
          this.$emit('tracking', this.trackingObject === null ? {} : this.trackingObject)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-cross-sell {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: rhythm();
    border-radius: radius(large);
    overflow: hidden;

    .icon {
      margin: 0 0 rhythm(small);
    }

    ul,
    p,
    h4 {
      margin: 0 0 1.25rem;
    }

    p {
      @include type-body-m;
    }

    .illustration {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-shrink: 0;
      width: rem(128px);
      height: rem(128px);
      margin: 0 0 rhythm();
      border-radius: radius(small);

      ::v-deep img {
        width: auto;
        height: auto;
        min-width: 100%;
        min-height: 100%;
      }

      ::v-deep svg {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }

      &--on-clouds {
        margin: 0 0 rhythm(small);

        .clouds {
          position: absolute;
          top: - rhythm();
          left: - rhythm();
          width: rem(218px);
          height: unset;
          max-width: unset;
          max-height: unset;
          z-index: 4;
        }

        ::v-deep .zg-illustration {
          position: relative;
          width: rem(88px);
          height: rem(88px);
          z-index: 5;
        }
      }
    }

    .title {
      margin: 0;

      h4 {
        display: flex;
        flex-direction: row;
        gap: rem(8px);
        transition: all 0.3s ease;
        justify-content: space-between;
        align-items: center;

        .zg-icon {
          flex-shrink: 0;
          margin: rem(3px) 0 0;
          transition: all 0.3s ease;
        }
      }
    }

    .button {
      height: auto;
    }
  }

  // ? ----------------------------------------------------------------------
  // states
  // ? ----------------------------------------------------------------------

  .zg-cross-sell {
    &:focus {
      box-shadow: box-shadow(focus);
    }

    &:focus:not(:focus-visible) {
      box-shadow: none;
    }
  }

  .full-link:hover {
    .title {
      h4 {
        color: color(brand-500);

        .zg-icon {
          fill: color(brand-500);
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // background
  // ? ----------------------------------------------------------------------

  .zg-cross-sell--background-beige {
    background-color: color(beige);
  }

  .zg-cross-sell--background-white {
    background-color: color(white);
  }

  .zg-cross-sell--background-brand-100 {
    background-color: color(brand-100);
  }

  a {
    border: none;
    margin-top: auto;
    height: 100%;
  }
</style>
